import React, { useState, useEffect } from 'react';
import { useThemeColor } from '../../hooks/useThemeColor';
import { Search } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import { DEFAULT_LANGUAGE } from '../../locales/translations';

const Language = () => {
  const { aispaceColor } = useThemeColor();
  const { language: savedLanguage, setLanguage, availableLanguages, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(savedLanguage);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Keep the radio selection in sync when the saved language changes externally
  // (e.g. when switching to this tab after saving elsewhere, or on first mount
  // once the context has resolved the initial language from localStorage).
  useEffect(() => {
    setSelectedLanguage(savedLanguage);
  }, [savedLanguage]);

  const hasChanges = selectedLanguage !== savedLanguage;

  // Filter languages based on search
  const filteredLanguages = availableLanguages.filter(lang =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredLanguages.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLanguages = filteredLanguages.slice(startIndex, startIndex + itemsPerPage);

  const handleSave = () => {
    setLanguage(selectedLanguage);
  };

  const handleCancel = () => {
    setSelectedLanguage(savedLanguage);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-[18px] font-semibold text-[#111827]">{t('language.pageTitle')}</h2>
          <p className="text-[14px] text-[#6B7280] mt-1">{t('language.pageSubtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-[14px] text-[#6B7280]">{t('language.unsavedChanges')}</span>
          )}
          <button
            onClick={handleCancel}
            disabled={!hasChanges}
            className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('language.cancelButton')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="px-4 py-2 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: aispaceColor }}
          >
            {t('language.saveButton')}
          </button>
        </div>
      </div>

      {/* Language List Container */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        {/* Search */}
        <div className="px-6 py-4">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder={t('language.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none transition-colors"
              onFocus={(e) => (e.target.style.borderColor = aispaceColor)}
              onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
            />
          </div>
        </div>

        {/* Language Options */}
        <div className="px-6 pb-4 space-y-3">
          {paginatedLanguages.map((lang) => {
            const isSelected = selectedLanguage === lang.code;
            const isCurrent = savedLanguage === lang.code;
            const isDefault = lang.code === DEFAULT_LANGUAGE;

            return (
              <button
                key={lang.code}
                onClick={() => setSelectedLanguage(lang.code)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                  isSelected
                    ? 'bg-opacity-5'
                    : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                }`}
                style={isSelected ? { borderColor: aispaceColor, backgroundColor: `${aispaceColor}08` } : {}}
              >
                {/* Radio */}
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-current' : 'border-[#D1D5DB]'
                  }`}
                  style={isSelected ? { borderColor: aispaceColor } : {}}
                >
                  {isSelected && (
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: aispaceColor }}
                    />
                  )}
                </div>

                {/* Flag */}
                <span className="text-[28px]">{lang.flag}</span>

                {/* Language Name */}
                <div className="flex-1">
                  <p className="text-[15px] font-medium text-[#111827]">{lang.name}</p>
                  <p className="text-[13px] text-[#6B7280]">{lang.nativeName}</p>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2">
                  {isDefault && (
                    <span className="px-3 py-1 text-[12px] font-medium text-[#374151] bg-[#F3F4F6] border border-[#E5E7EB] rounded-full">
                      {t('language.defaultBadge')}
                    </span>
                  )}
                  {isCurrent && (
                    <span
                      className="px-3 py-1 text-[12px] font-medium text-white rounded-full"
                      style={{ backgroundColor: aispaceColor }}
                    >
                      {t('language.currentBadge')}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E7EB]">
          <span className="text-[14px] text-[#6B7280]">
            {t('pagination.pageOf', { current: currentPage, total: totalPages || 1 })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.back')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Language;
